package dev.lingvo.shared.screenshots.frames

import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.Insets

/** Keeps all frame lengths in screen-width units so any capture aspect retains its pixels. */
data class FrameSpec(
    val kind: FrameKind,
    val bezel: Insets,
    val rim: Double,
    val screenRadius: Double,
    val smoothing: Double,
    val cutout: Cutout? = null,
    val buttons: List<Button> = emptyList(),
    val extras: List<Extra> = emptyList(),
)

/** Separates capture overlays from the bezel so screenshots never need to contain hardware cutouts. */
sealed interface Cutout {
    /** Rounded pill overlays avoid product-specific camera artwork. */
    data class Island(val w: Double, val h: Double, val top: Double) : Cutout
    /** Shoulder and bottom radii preserve the top-connected notch silhouette. */
    data class Notch(val w: Double, val h: Double, val bottomRadius: Double, val shoulder: Double) : Cutout
    /** Screen-relative centres allow both central and offset punch holes. */
    data class Hole(val d: Double, val cx: Double, val top: Double) : Cutout
}

/** Side positions are measured before rotating portrait hardware into landscape. */
enum class Side { TOP, RIGHT, BOTTOM, LEFT }

/** Side fractions scale along the body, while protrusion and radius scale with screen width. */
data class Button(val side: Side, val from: Double, val to: Double, val protrusion: Double, val radius: Double? = null)

/** Named structural extras let the table describe devices without platform assets. */
enum class Extra { CAMERA, CREASE, BAND_STUBS, LAPTOP_BASE, BROWSER_BAR, TV_STAND }

/** Provides the SPEC-B table independently of painting and store-class choices. */
object FrameSpecs {
    /** An exhaustive branch makes adding a kind require a deliberate geometric specification. */
    fun of(kind: FrameKind): FrameSpec = when (kind) {
        FrameKind.PHONE_ISLAND -> FrameSpec(kind, all(.050), .014, .125, .6,
            Cutout.Island(.290, .084, .028), phoneButtons(.155, .009))
        FrameKind.PHONE_NOTCH -> FrameSpec(kind, all(.055), .014, .110, .6,
            Cutout.Notch(.420, .075, .045, .018), phoneButtons(.160, .009))
        FrameKind.PHONE_PUNCH_HOLE -> FrameSpec(kind, all(.040), .010, .085, .3,
            Cutout.Hole(.036, .5, .040), androidButtons())
        FrameKind.PHONE_FLAT -> FrameSpec(kind, all(.045), .010, .060, .2, buttons = androidButtons())
        FrameKind.TABLET_UNIFORM -> FrameSpec(kind, all(.040), .008, .022, .6,
            buttons = listOf(Button(Side.TOP, .80, .90, .006), Button(Side.RIGHT, .06, .12, .006), Button(Side.RIGHT, .13, .19, .006)),
            extras = listOf(Extra.CAMERA))
        FrameKind.TABLET_ANDROID -> FrameSpec(kind, all(.035), .006, .030, .2,
            buttons = listOf(Button(Side.RIGHT, .08, .16, .006), Button(Side.RIGHT, .18, .22, .006)), extras = listOf(Extra.CAMERA))
        FrameKind.FOLDABLE_OPEN -> FrameSpec(kind, all(.028), .008, .035, .3, Cutout.Hole(.022, .78, .030),
            listOf(Button(Side.RIGHT, .20, .30, .007), Button(Side.RIGHT, .32, .36, .007)), listOf(Extra.CREASE))
        FrameKind.WATCH_RECT -> FrameSpec(kind, all(.115), .030, .200, .6,
            buttons = listOf(Button(Side.RIGHT, .24, .36, .070, .03), Button(Side.RIGHT, .55, .75, .035)))
        FrameKind.WATCH_ROUND -> FrameSpec(kind, all(.100), .015, .5, 0.0,
            buttons = listOf(Button(Side.RIGHT, .43, .57, .060)))
        FrameKind.LAPTOP -> FrameSpec(kind, Insets(.035, .022, .050, .022), .004, .006, 0.0,
            extras = listOf(Extra.LAPTOP_BASE, Extra.CAMERA))
        FrameKind.BROWSER -> FrameSpec(kind, Insets(.050, .002, .002, .002), 0.0, .014, 0.0,
            extras = listOf(Extra.BROWSER_BAR))
        FrameKind.TV -> FrameSpec(kind, Insets(.012, .012, .022, .012), .002, .004, 0.0, extras = listOf(Extra.TV_STAND))
        FrameKind.NONE -> FrameSpec(kind, all(0.0), 0.0, .040, .6)
    }

    private fun all(n: Double) = Insets(n, n, n, n)
    private fun androidButtons() = listOf(Button(Side.RIGHT, .20, .30, .008), Button(Side.RIGHT, .33, .38, .008))
    private fun phoneButtons(first: Double, protrusion: Double) = listOf(
        Button(Side.LEFT, first, first + .030, protrusion), Button(Side.LEFT, .225, .285, protrusion),
        Button(Side.LEFT, .305, .365, protrusion), Button(Side.RIGHT, .250, .360, protrusion),
    )
}
